import React, { useState, useEffect, useCallback } from 'react';
import { ManagedImage } from './ManagedImage';

export const ProjectGallery = ({ projects }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const openProject = (project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
  };
  const closeProject = () => setSelectedProject(null);

  const step = useCallback(
    (dir) => (e) => {
      if (e) e.stopPropagation();
      setCurrentImageIndex((i) => {
        const n = selectedProject.images.length;
        return (i + dir + n) % n;
      });
    },
    [selectedProject]
  );

  useEffect(() => {
    if (!selectedProject) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeProject();
      if (e.key === 'ArrowLeft') step(-1)();
      if (e.key === 'ArrowRight') step(1)();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedProject, step]);

  const hasMultiple = selectedProject && selectedProject.images.length > 1;
  const current = selectedProject && selectedProject.images[currentImageIndex];

  return (
    <>
      <div className="grid gap-7" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%, max(140px, 28%)),1fr))' }}>
        {projects.map((project, index) => (
          <div key={index} onClick={() => openProject(project)} className="cursor-pointer flex flex-col gap-3 group">
            <div className="relative overflow-hidden bg-cv-panel" style={{ aspectRatio: '1/1', borderRadius: 'var(--cv-radius)' }}>
              <ManagedImage
                url={project.cover.url}
                basePath={project.cover.basePath}
                filename={project.cover.filename}
                alt={project.cover.alt || project.title}
                fit={project.cover.fit}
                focusX={project.cover.focusX}
                focusY={project.cover.focusY}
                className="w-full h-full transition-transform duration-[600ms] group-hover:scale-[1.04]"
              />
              {project.images.length > 1 && (
                <span className="absolute bottom-2.5 right-2.5 text-xs tracking-[0.1em] text-cv-muted" style={{ background: 'rgba(250,247,242,0.92)', padding: '4px 10px', borderRadius: 'var(--cv-radius)' }}>
                  {project.images.length} bilder
                </span>
              )}
            </div>
            <h3 className="m-0 font-serif font-medium text-[21px] text-cv-ink">{project.title}</h3>
          </div>
        ))}
      </div>

      {selectedProject && (
        <div onClick={closeProject} className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-5" style={{ background: 'rgba(38,33,27,0.94)', padding: 'clamp(16px,4vw,40px)' }}>
          <div className="relative flex items-center justify-center w-full">
            {hasMultiple && (
              <button onClick={step(-1)} aria-label="Föregående" className="absolute z-[2] rounded-full text-cv-base cursor-pointer flex items-center justify-center" style={{ left: 'clamp(4px,1.5vw,40px)', width: 52, height: 52, background: 'rgba(38,33,27,0.55)', border: '1px solid rgba(250,247,242,0.3)' }}>←</button>
            )}
            <div onClick={(e) => e.stopPropagation()} className="flex items-center justify-center" style={{ maxWidth: 'min(86vw, 900px)', maxHeight: '72vh' }}>
              <ManagedImage
                url={current.url}
                basePath={current.basePath}
                filename={current.filename}
                alt={current.alt || selectedProject.title}
                fit="contain"
                style={{ maxWidth: 'min(86vw, 900px)', maxHeight: '72vh', width: 'auto', height: 'auto', borderRadius: 'var(--cv-radius)', boxShadow: '0 32px 80px rgba(0,0,0,0.5)' }}
              />
            </div>
            {hasMultiple && (
              <button onClick={step(1)} aria-label="Nästa" className="absolute z-[2] rounded-full text-cv-base cursor-pointer flex items-center justify-center" style={{ right: 'clamp(4px,1.5vw,40px)', width: 52, height: 52, background: 'rgba(38,33,27,0.55)', border: '1px solid rgba(250,247,242,0.3)' }}>→</button>
            )}
          </div>

          <div className="text-center flex flex-col gap-1.5">
            <span className="font-serif text-[22px] text-cv-base">{selectedProject.title}</span>
            {hasMultiple && (
              <span className="text-[13px] tracking-[0.2em] text-cv-faint">{currentImageIndex + 1} / {selectedProject.images.length}</span>
            )}
          </div>

          <button onClick={closeProject} aria-label="Stäng" className="absolute text-cv-base text-[28px] cursor-pointer leading-none" style={{ top: 20, right: 'clamp(14px,4vw,32px)', background: 'none', border: 'none' }}>×</button>
        </div>
      )}
    </>
  );
};
